import React, { useEffect, useState } from "react";
import "./NewsletterSubscribers.css";
import { backend_url } from "../../App";

const NewsletterSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  // Health check function to test backend connectivity
  const testBackendHealth = async () => {
    try {
      console.log("=== BACKEND HEALTH TESTS ===");

      // Test 1: Basic connectivity
      console.log("1. Testing basic connectivity...");
      const basicResponse = await fetch(`${backend_url}/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("Basic connectivity - Status:", basicResponse.status);
      const basicText = await basicResponse.text();
      console.log("Basic connectivity - Response:", basicText);

      // Test 2: CORS test endpoint (no auth)
      console.log("2. Testing CORS without auth...");
      const corsResponse = await fetch(`${backend_url}/cors-test`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      console.log("CORS test - Status:", corsResponse.status);
      console.log(
        "CORS test - Headers:",
        Object.fromEntries(corsResponse.headers.entries())
      );
      const corsData = await corsResponse.json();
      console.log("CORS test - Response:", corsData);

      // Test 3: Admin CORS test endpoint (with auth)
      console.log("3. Testing admin CORS with auth...");
      const token = localStorage.getItem("auth-token");
      if (token) {
        const adminCorsResponse = await fetch(
          `${backend_url}/admin/cors-test`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "auth-token": token,
            },
            credentials: "include",
          }
        );
        console.log("Admin CORS test - Status:", adminCorsResponse.status);
        console.log(
          "Admin CORS test - Headers:",
          Object.fromEntries(adminCorsResponse.headers.entries())
        );
        const adminCorsData = await adminCorsResponse.json();
        console.log("Admin CORS test - Response:", adminCorsData);
      } else {
        console.log("Admin CORS test - No auth token found");
      }

      console.log("=== HEALTH TESTS COMPLETE ===");
      return basicResponse.ok;
    } catch (error) {
      console.error("Backend health check failed:", error);
      return false;
    }
  };

  const fetchSubscribers = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("auth-token");
      if (!token) {
        setError("Please login to view subscribers");
        setLoading(false);
        return;
      }

      console.log(
        "Fetching subscribers with token:",
        token ? "Present" : "Missing"
      );
      console.log("Backend URL:", backend_url);

      const response = await fetch(
        `${backend_url}/admin/newsletter/subscribers`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "auth-token": token,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      console.log("Response status:", response.status);
      console.log(
        "Response headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Response data:", data);

      if (data.success) {
        setSubscribers(data.subscribers);
        setTotalCount(data.totalCount);
      } else {
        setError(data.message || "Failed to fetch subscribers");
      }
    } catch (error) {
      console.error("Error fetching subscribers:", error);

      // Retry logic for network errors (up to 2 retries)
      if (
        retryCount < 2 &&
        (error.name === "TypeError" ||
          error.message.includes("Failed to fetch"))
      ) {
        console.log(`Retrying... Attempt ${retryCount + 1}`);
        // Test backend health before retry
        const isHealthy = await testBackendHealth();
        console.log("Backend health check result:", isHealthy);

        setTimeout(() => {
          fetchSubscribers(retryCount + 1);
        }, 2000); // Wait 2 seconds before retry
        return;
      }

      // Specific error messages based on error type
      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        setError(
          "Unable to connect to server. The backend service might be starting up. Please wait a moment and try again."
        );
      } else if (error.message.includes("401")) {
        setError("Authentication failed. Please login again.");
      } else if (error.message.includes("403")) {
        setError("Access denied. Admin privileges required.");
      } else {
        setError(`Network error: ${error.message}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (subscriberId, email) => {
    if (!window.confirm(`Are you sure you want to unsubscribe ${email}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("auth-token");
      const response = await fetch(
        `${backend_url}/admin/newsletter/unsubscribe/${subscriberId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "auth-token": token,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        // Remove subscriber from list
        setSubscribers((prev) =>
          prev.filter((sub) => sub._id !== subscriberId)
        );
        setTotalCount((prev) => prev - 1);
        alert("User unsubscribed successfully");
      } else {
        alert(data.message || "Failed to unsubscribe user");
      }
    } catch (error) {
      console.error("Error unsubscribing user:", error);
      alert("Network error. Please try again.");
    }
  };

  const exportSubscribers = () => {
    if (subscribers.length === 0) {
      alert("No subscribers to export");
      return;
    }

    const csvContent = [
      ["Email", "Subscribed Date", "Source"].join(","),
      ...subscribers.map((subscriber) =>
        [
          subscriber.email,
          new Date(subscriber.subscribedDate).toLocaleDateString(),
          subscriber.source || "website",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-subscribers-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  if (loading) {
    return (
      <div className="newsletter-subscribers">
        <div className="loading">Loading subscribers...</div>
      </div>
    );
  }

  return (
    <div className="newsletter-subscribers">
      <div className="newsletter-header">
        <h2>Newsletter Subscribers</h2>
        <div className="header-actions">
          <span className="subscriber-count">
            Total: {totalCount} subscribers
          </span>
          <button className="export-btn" onClick={exportSubscribers}>
            Export CSV
          </button>
          <button className="refresh-btn" onClick={fetchSubscribers}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {subscribers.length === 0 && !loading && !error && (
        <div className="no-subscribers">
          <p>No newsletter subscribers found.</p>
        </div>
      )}

      {subscribers.length > 0 && (
        <div className="subscribers-table-container">
          <table className="subscribers-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Subscribed Date</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((subscriber) => (
                <tr key={subscriber._id}>
                  <td className="email-cell">{subscriber.email}</td>
                  <td>
                    {new Date(subscriber.subscribedDate).toLocaleDateString()}
                  </td>
                  <td className="source-cell">
                    {subscriber.source || "website"}
                  </td>
                  <td>
                    <button
                      className="unsubscribe-btn"
                      onClick={() =>
                        handleUnsubscribe(subscriber._id, subscriber.email)
                      }
                    >
                      Unsubscribe
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NewsletterSubscribers;
